import type { ReportConfig, ReportData } from "../types.js";

/**
 * Generate a self-contained HTML report with inline CSS.
 */
export function generateHtmlReport(
  data: ReportData,
  config: ReportConfig,
): string {
  const completePct =
    data.summary.featuresTotal > 0
      ? Math.round(
          (data.summary.featuresComplete / data.summary.featuresTotal) * 100,
        )
      : 0;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Project Report: ${esc(data.project.name)}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1a1a2e;
      background: #f8f9fc;
      line-height: 1.6;
      padding: 0;
    }

    .container {
      max-width: 860px;
      margin: 0 auto;
      padding: 40px 32px;
    }

    header {
      border-bottom: 3px solid #2d3561;
      padding-bottom: 20px;
      margin-bottom: 36px;
    }

    header h1 {
      font-size: 28px;
      font-weight: 700;
      color: #2d3561;
      margin-bottom: 8px;
    }

    header .meta {
      font-size: 14px;
      color: #6c7293;
    }

    h2 {
      font-size: 20px;
      font-weight: 600;
      color: #2d3561;
      margin-top: 36px;
      margin-bottom: 16px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e0e4ef;
    }

    h3 {
      font-size: 16px;
      font-weight: 600;
      color: #4a5078;
      margin-top: 24px;
      margin-bottom: 12px;
    }

    /* Summary Cards */
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 16px;
      margin-bottom: 8px;
    }

    .summary-card {
      background: white;
      border-radius: 10px;
      padding: 20px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    }

    .summary-card .label {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #6c7293;
      margin-bottom: 4px;
    }

    .summary-card .value {
      font-size: 24px;
      font-weight: 700;
      color: #2d3561;
    }

    .summary-card .sub {
      font-size: 13px;
      color: #8b90b0;
      margin-top: 2px;
    }

    /* Progress Bar */
    .progress-bar {
      width: 100%;
      height: 8px;
      background: #e0e4ef;
      border-radius: 4px;
      margin-top: 8px;
      overflow: hidden;
    }

    .progress-bar .fill {
      height: 100%;
      border-radius: 4px;
      transition: width 0.3s ease;
    }

    .fill-green { background: #22c55e; }
    .fill-blue { background: #3b82f6; }
    .fill-amber { background: #f59e0b; }

    /* Tables */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
      background: white;
      border-radius: 10px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    }

    th {
      background: #f1f3f9;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #6c7293;
      text-align: left;
      padding: 12px 16px;
      border-bottom: 1px solid #e0e4ef;
    }

    td {
      padding: 12px 16px;
      font-size: 14px;
      border-bottom: 1px solid #f1f3f9;
    }

    tr:last-child td {
      border-bottom: none;
    }

    tr:hover td {
      background: #fafbfe;
    }

    .text-right { text-align: right; }
    .text-center { text-align: center; }

    /* Status badges */
    .badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      line-height: 1.4;
    }

    .badge-complete {
      background: #dcfce7;
      color: #15803d;
    }

    .badge-in-progress {
      background: #dbeafe;
      color: #1d4ed8;
    }

    .badge-pending {
      background: #f3f4f6;
      color: #6b7280;
    }

    /* Metric list */
    .velocity-list {
      list-style: none;
      background: white;
      border-radius: 10px;
      padding: 20px 24px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
    }

    .velocity-list li {
      padding: 8px 0;
      font-size: 15px;
      border-bottom: 1px solid #f1f3f9;
    }

    .velocity-list li:last-child {
      border-bottom: none;
    }

    .velocity-list .metric-label {
      font-weight: 600;
      color: #4a5078;
    }

    .velocity-list .trend-up { color: #22c55e; }
    .velocity-list .trend-down { color: #ef4444; }
    .velocity-list .trend-stable { color: #6c7293; }

    /* Footer */
    footer {
      margin-top: 48px;
      padding-top: 20px;
      border-top: 1px solid #e0e4ef;
      font-size: 13px;
      color: #8b90b0;
      text-align: center;
    }

    @media print {
      body { background: white; }
      .container { padding: 20px 0; }
      .summary-card, table, .velocity-list {
        box-shadow: none;
        border: 1px solid #e0e4ef;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <h1>Project Report: ${esc(data.project.name)}</h1>
      <div class="meta">
        Report Date: ${esc(data.project.reportDate)}${
          config.dateRange
            ? ` &middot; Period: ${esc(data.project.startDate)} &ndash; ${esc(data.project.reportDate)}`
            : ""
        }
      </div>
    </header>

    <h2>Summary</h2>
    <div class="summary-grid">
      <div class="summary-card">
        <div class="label">Features</div>
        <div class="value">${data.summary.featuresComplete} / ${data.summary.featuresTotal}</div>
        <div class="sub">${completePct}% complete</div>
        <div class="progress-bar">
          <div class="fill fill-green" style="width: ${completePct}%"></div>
        </div>
      </div>
      <div class="summary-card">
        <div class="label">Total Cost</div>
        <div class="value">$${data.summary.totalCost.toFixed(2)}</div>
        <div class="sub">${data.summary.totalTokens.toLocaleString()} tokens</div>
      </div>
      ${
        data.summary.budgetUsed > 0
          ? `<div class="summary-card">
        <div class="label">Budget Used</div>
        <div class="value">${data.summary.budgetUsed}%</div>
        <div class="progress-bar">
          <div class="fill ${data.summary.budgetUsed > 90 ? "fill-amber" : "fill-blue"}" style="width: ${Math.min(data.summary.budgetUsed, 100)}%"></div>
        </div>
      </div>`
          : ""
      }
      ${
        data.summary.estimateAccuracy > 0
          ? `<div class="summary-card">
        <div class="label">Estimate Accuracy</div>
        <div class="value">${data.summary.estimateAccuracy}%</div>
        <div class="progress-bar">
          <div class="fill ${data.summary.estimateAccuracy >= 80 ? "fill-green" : "fill-amber"}" style="width: ${data.summary.estimateAccuracy}%"></div>
        </div>
      </div>`
          : ""
      }
    </div>

${config.includeFeatureDetails && data.features.length > 0 ? renderFeatureTable(data) : ""}
${config.includeCostBreakdown ? renderCostBreakdown(data) : ""}
${config.includeVelocityMetrics ? renderVelocity(data) : ""}
${config.includeEstimateAccuracy && data.estimateAccuracy.byComplexity.length > 0 ? renderAccuracy(data) : ""}

    <footer>
      Generated by Fathom &mdash; AI Workflow Intelligence
    </footer>
  </div>
</body>
</html>`;
}

// ── Section Renderers ────────────────────────────────────────

function renderFeatureTable(data: ReportData): string {
  const rows = data.features
    .map(
      (f) => `      <tr>
        <td>${esc(f.name)}</td>
        <td class="text-center">${statusBadge(f.status)}</td>
        <td class="text-right">$${f.estimatedCost.toFixed(2)}</td>
        <td class="text-right">$${f.actualCost.toFixed(2)}</td>
        <td class="text-center">${f.sessions}</td>
        <td class="text-right">${f.daysElapsed.toFixed(1)}</td>
      </tr>`,
    )
    .join("\n");

  return `    <h2>Feature Progress</h2>
    <table>
      <thead>
        <tr>
          <th>Feature</th>
          <th class="text-center">Status</th>
          <th class="text-right">Est. Cost</th>
          <th class="text-right">Actual Cost</th>
          <th class="text-center">Sessions</th>
          <th class="text-right">Days</th>
        </tr>
      </thead>
      <tbody>
${rows}
      </tbody>
    </table>
`;
}

function renderCostBreakdown(data: ReportData): string {
  let html = "    <h2>Cost Breakdown</h2>\n";

  if (data.costBreakdown.byModel.length > 0) {
    const rows = data.costBreakdown.byModel
      .map(
        (m) => `      <tr>
        <td>${esc(m.model)}</td>
        <td class="text-right">${formatTokens(m.tokens)}</td>
        <td class="text-right">$${m.cost.toFixed(2)}</td>
        <td class="text-right">${m.percentage}%</td>
      </tr>`,
      )
      .join("\n");

    html += `    <h3>By Model</h3>
    <table>
      <thead>
        <tr>
          <th>Model</th>
          <th class="text-right">Tokens</th>
          <th class="text-right">Cost</th>
          <th class="text-right">% of Total</th>
        </tr>
      </thead>
      <tbody>
${rows}
      </tbody>
    </table>
`;
  }

  if (data.costBreakdown.byFeature.length > 0) {
    const rows = data.costBreakdown.byFeature
      .map(
        (f) => `      <tr>
        <td>${esc(f.feature)}</td>
        <td class="text-right">$${f.cost.toFixed(2)}</td>
        <td class="text-right">${f.percentage}%</td>
      </tr>`,
      )
      .join("\n");

    html += `    <h3>By Feature</h3>
    <table>
      <thead>
        <tr>
          <th>Feature</th>
          <th class="text-right">Cost</th>
          <th class="text-right">% of Total</th>
        </tr>
      </thead>
      <tbody>
${rows}
      </tbody>
    </table>
`;
  }

  return html;
}

function renderVelocity(data: ReportData): string {
  const trendClass =
    data.velocity.trend === "improving"
      ? "trend-up"
      : data.velocity.trend === "declining"
        ? "trend-down"
        : "trend-stable";

  const trendLabel =
    data.velocity.trend === "improving"
      ? "\u2191 improving"
      : data.velocity.trend === "declining"
        ? "\u2193 declining"
        : "\u2192 stable";

  return `    <h2>Velocity</h2>
    <ul class="velocity-list">
      <li><span class="metric-label">Throughput:</span> ${data.velocity.featuresPerWeek} features/week <span class="${trendClass}">(${trendLabel})</span></li>
      <li><span class="metric-label">Avg Cost/Feature:</span> $${data.velocity.avgCostPerFeature.toFixed(2)}</li>
      <li><span class="metric-label">Avg Time/Feature:</span> ${data.velocity.avgDaysPerFeature} days</li>
    </ul>
`;
}

function renderAccuracy(data: ReportData): string {
  const rows = data.estimateAccuracy.byComplexity
    .map(
      (c) => `      <tr>
        <td>${esc(c.complexity)}</td>
        <td class="text-right">${c.accuracy}%</td>
        <td class="text-center">${c.dataPoints}</td>
      </tr>`,
    )
    .join("\n");

  return `    <h2>Estimate Accuracy</h2>
    <table>
      <thead>
        <tr>
          <th>Complexity</th>
          <th class="text-right">Accuracy</th>
          <th class="text-center">Data Points</th>
        </tr>
      </thead>
      <tbody>
${rows}
      </tbody>
    </table>
`;
}

// ── Helpers ──────────────────────────────────────────────────

function esc(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function statusBadge(status: string): string {
  switch (status) {
    case "complete":
      return '<span class="badge badge-complete">\u2713 Complete</span>';
    case "in_progress":
      return '<span class="badge badge-in-progress">In Progress</span>';
    case "pending":
      return '<span class="badge badge-pending">Pending</span>';
    default:
      return `<span class="badge">${esc(status)}</span>`;
  }
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(1)}M`;
  }
  if (tokens >= 1_000) {
    return `${(tokens / 1_000).toFixed(0)}K`;
  }
  return tokens.toString();
}
